import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import { ProfileCompletionStatus } from '@/types';
import { ProfileService } from '@/utils/profile-service';

interface ProfileCompletionIndicatorProps {
  completionStatus: ProfileCompletionStatus;
  showDetails?: boolean;
}

export const ProfileCompletionIndicator: React.FC<ProfileCompletionIndicatorProps> = ({
  completionStatus,
  showDetails = false,
}) => {
  const { isComplete, completedFields, missingFields, completionPercentage } = completionStatus;

  const getProgressColor = (percentage: number) => {
    if (percentage === 100) return 'success';
    if (percentage >= 70) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Profile Completion
          </Typography>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            color={getProgressColor(completionPercentage)}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        <Chip
          label={`${completionPercentage}%`}
          color={isComplete ? 'success' : 'default'}
          size="small"
        />
      </Box>

      {!isComplete && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Complete Your Profile</AlertTitle>
          Complete your profile to make the best impression on potential guests and improve your listing visibility.
        </Alert>
      )}

      {showDetails && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Profile Fields
          </Typography>
          <List dense>
            {[...completedFields, ...missingFields].map((field) => {
              const isCompleted = completedFields.includes(field);
              return (
                <ListItem key={field} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {isCompleted ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={ProfileService.getFieldDisplayName(field)}
                    primaryTypographyProps={{
                      color: isCompleted ? 'text.primary' : 'text.secondary',
                      variant: 'body2',
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default ProfileCompletionIndicator;